/**
 * @file: useQrcodeAuth.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AuthNavParams } from '@euler/app/flows/auth/Auth';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useLogin, userAccountSwitched$ } from '@euler/app/flows/auth/hooks';
import { useAppLoading } from '@euler/components/loading/Provider';
import {
  AuthProviderType,
  AuthenticatorNextActionApp,
} from '@euler/model/auth';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorAlert, showAlert, withConfirmation } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useRef } from 'react';

export const useQrcodeAuth = (options?: {
  onAppSelection?: (params: {
    apps: AuthenticatorNextActionApp[];
    authToken: string;
    continueWith: (appId: IdType) => Promise<void>;
  }) => void;
  /** The scanner's title; signing in is the default use. */
  title?: string;
  /**
   * Gets a code that is not a sign-in code first; true when it took the
   * code (a customer's coupon), so the scan is not read as a sign-in.
   */
  onOtherCode?: (value: string) => boolean;
}) => {
  const loading = useAppLoading();
  const navigation = useNavigation<StackNavigationProp<AuthNavParams>>();

  const loginRef = useRef<ReturnType<typeof useLogin>['login']>();
  const pendingAuthTokenRef = useRef<string>();

  const { login } = useLogin({
    onAppSelection: (apps, authToken) => {
      pendingAuthTokenRef.current = authToken;
      options?.onAppSelection?.({
        apps,
        authToken,
        continueWith: async (appId: IdType) => {
          loading.show();
          try {
            const result = await loginRef.current?.({
              type: AuthProviderType.TokenExchange,
              authToken: pendingAuthTokenRef.current!,
              action: { type: 'app-targeting', appId },
            });
            __DEV__ &&
              console.log('qrcode auth token exchange result: ', result);
            if (result != null) {
              userAccountSwitched$.next(result.results.user.uid);
            }
          } finally {
            loading.hide();
          }
        },
      });
    },
  });

  loginRef.current = login;

  const { userService } = useServiceFactory();
  const currentUserId = useCurrentUser()?.user.uid;

  const scanQrcode = usePersistFn(() => {
    navigation.navigate('_qrcodeScan', {
      title: options?.title ?? '扫码登录',
      onScan: async value => {
        __DEV__ && console.log('qrcode scanned value: ', value);
        if (options?.onOtherCode?.(value)) return;
        const [type, code] = value.split(':');
        if (type === 'qrcode-auth') {
          if (code?.length !== 40) {
            void showAlert('无效二维码!');
            return;
          }
          loading.show();
          try {
            const result = await login({
              type: AuthProviderType.Qrcode,
              code,
            });
            __DEV__ && console.log('qrcode auth result: ', result);
            if (
              result == null ||
              result.nextAction?.type === 'app-selection' ||
              result.results.user.uid === currentUserId
            ) {
              return;
            }
            userAccountSwitched$.next(
              currentUserId ? result.results.user.uid : '',
            );
          } finally {
            loading.hide();
          }
        } else if (type === 'qrcode-auth-challenge') {
          if (code?.length !== 40) {
            void showAlert('无效二维码!');
            return;
          }

          if (!currentUserId) {
            void showAlert('错误', '登录后才能扫码授权该设备登录!');
            return;
          }

          try {
            const { deviceName } =
              await userService.qrcodeAuthChallengeScanned(code);

            const confirmed = await withConfirmation({
              title: `您正在登录"${
                deviceName === 'web' ? '知车网页版' : deviceName
              }", 是否确认?`,
            });
            if (confirmed) {
              await userService
                .qrcodeAuthChallengeConfirmed(code)
                .catch(onErrorAlert);
            } else {
              await userService
                .qrcodeAuthChallengeCanceled(code)
                .catch(onErrorAlert);
            }
          } catch (err) {
            void showAlert((err as any).message);
          }
        }
      },
    });
  });

  const qrcodeChallenge = usePersistFn(() => {
    navigation.navigate('_qrcodeChallenge', {});
  });

  return { scanQrcode, qrcodeChallenge };
};
